import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, 
  Briefcase, 
  FileText, 
  MessageSquare, 
  Star,
  TrendingUp,
  Shield,
  Crown,
  Building2,
  ClipboardList,
  CheckCircle,
  XCircle,
  Download,
  Loader2,
  Lock,
  Archive,
  Trash2,
  Wrench
} from 'lucide-react';

function StatCard({ label, value, icon: Icon, trend, loading, onClick, testId }) {
  const clickable = typeof onClick === 'function';

  return (
    <Card
      className={`bg-white/5 backdrop-blur-md border border-white/5 ${clickable ? 'cursor-pointer hover:border-primary/40 hover:bg-white/10 transition-colors' : ''}`}
      onClick={onClick}
      data-testid={testId}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-1">{label}</p>
            <div className="flex items-baseline gap-2">
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              ) : (
                <h3 className="text-3xl font-bold text-white">{value}</h3>
              )}
              {trend && (
                <span className="text-xs text-green-400 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  {trend}
                </span>
              )}
            </div>
          </div>
          {Icon && (
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState('all');
  const [memberSort, setMemberSort] = useState('newest');
  const [bartendersSearch, setBartendersSearch] = useState('');
  const [bartendersSort, setBartendersSort] = useState('newest');
  const [ownersSearch, setOwnersSearch] = useState('');
  const [ownersSort, setOwnersSort] = useState('newest');
  const [jobsSearch, setJobsSearch] = useState('');
  const [jobsStatusFilter, setJobsStatusFilter] = useState('all');
  const [jobsSort, setJobsSort] = useState('newest');
  const [instantSearch, setInstantSearch] = useState('');
  const [instantStatusFilter, setInstantStatusFilter] = useState('all');
  const [instantSort, setInstantSort] = useState('newest');
  const [adminJobs, setAdminJobs] = useState([]);
  const [adminJobsTotal, setAdminJobsTotal] = useState(0);
  const [adminJobsLoading, setAdminJobsLoading] = useState(false);
  const [adminJobsError, setAdminJobsError] = useState('');
  const [reviewModerationRows, setReviewModerationRows] = useState([]);
  const [reviewModerationLoading, setReviewModerationLoading] = useState(false);
  const [reviewModerationError, setReviewModerationError] = useState('');
  const [reviewModerationSearch, setReviewModerationSearch] = useState('');
  const [reviewModerationStatusFilter, setReviewModerationStatusFilter] = useState('pending');
  const [reviewModerationSort, setReviewModerationSort] = useState('newest');
  const [reviewModerationNotes, setReviewModerationNotes] = useState({});
  const [verificationAdminNotes, setVerificationAdminNotes] = useState({});
  const [ownerLeads, setOwnerLeads] = useState([]);
  const [ownerLeadsTotal, setOwnerLeadsTotal] = useState(0);
  const [ownerLeadsLoading, setOwnerLeadsLoading] = useState(false);
  const [ownerLeadsError, setOwnerLeadsError] = useState('');
  const [ownerLeadSearch, setOwnerLeadSearch] = useState('');
  const [ownerLeadStatusFilter, setOwnerLeadStatusFilter] = useState('all');
  const [ownerLeadSort, setOwnerLeadSort] = useState('newest');
  const [ownerLeadDraftStatuses, setOwnerLeadDraftStatuses] = useState({});
  const [ownerLeadDraftNotes, setOwnerLeadDraftNotes] = useState({});
  const [ownerLeadDraftLastContactDates, setOwnerLeadDraftLastContactDates] = useState({});
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState(null);
  const [selectedMemberActivity, setSelectedMemberActivity] = useState(null);
  const [selectedMemberVerificationTimeline, setSelectedMemberVerificationTimeline] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupTaskLoadingKey, setCleanupTaskLoadingKey] = useState('');
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [drilldownType, setDrilldownType] = useState('');
  const [drilldownItems, setDrilldownItems] = useState([]);
  const [drilldownTotal, setDrilldownTotal] = useState(0);
  const [drilldownTimeRange, setDrilldownTimeRange] = useState('all');

  const csvEscape = useCallback((value) => {
    if (value === null || value === undefined) return '';
    const normalized = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (/[",\n]/.test(normalized)) {
      return `"${normalized.replace(/"/g, '""')}"`;
    }
    return normalized;
  }, []);

  const exportRowsToCsv = useCallback((filenamePrefix, headers, rows) => {
    if (!rows || rows.length === 0) {
      toast.error(`No data available to export for ${filenamePrefix}`);
      return;
    }

    const headerRow = headers.map((header) => csvEscape(header.label)).join(',');
    const dataRows = rows.map((row) => headers.map((header) => csvEscape(row[header.key])).join(','));
    const csvContent = [headerRow, ...dataRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    anchor.href = url;
    anchor.setAttribute('download', `${filenamePrefix}-${timestamp}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);

    toast.success(`${filenamePrefix} CSV exported`);
  }, [csvEscape]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/stats');
      setStats(response.data);
      setLastUpdated(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      setMembersLoading(true);
      setMembersError('');
      const response = await axios.get('/admin/users', {
        params: {
          limit: 500,
          user_type: 'all',
        },
      });
      setMembers(response.data?.users || []);
      setMembersTotal(response.data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setMembersError('Unable to load user tables. Please refresh.');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const fetchAdminJobs = useCallback(async () => {
    try {
      setAdminJobsLoading(true);
      setAdminJobsError('');
      const response = await axios.get('/admin/jobs', {
        params: {
          limit: 500,
          time_range: 'all',
        },
      });
      setAdminJobs(response.data?.jobs || []);
      setAdminJobsTotal(response.data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch admin jobs:', err);
      setAdminJobsError('Unable to load jobs table. Please refresh.');
    } finally {
      setAdminJobsLoading(false);
    }
  }, []);

  const fetchReviewModerationRows = useCallback(async () => {
    try {
      setReviewModerationLoading(true);
      setReviewModerationError('');
      const response = await axios.get('/admin/reviews', {
        params: {
          limit: 300,
          status: reviewModerationStatusFilter,
          time_range: 'all',
        },
      });
      setReviewModerationRows(response.data?.reviews || []);
    } catch (err) {
      console.error('Failed to fetch moderation reviews:', err);
      setReviewModerationError('Unable to load review moderation queue.');
    } finally {
      setReviewModerationLoading(false);
    }
  }, [reviewModerationStatusFilter]);

  const fetchOwnerLeads = useCallback(async () => {
    try {
      setOwnerLeadsLoading(true);
      setOwnerLeadsError('');
      const response = await axios.get('/admin/owner-leads', {
        params: {
          limit: 500,
          search: ownerLeadSearch,
          status: ownerLeadStatusFilter,
          sort: ownerLeadSort,
        },
      });

      const rows = response.data?.owner_leads || [];
      setOwnerLeads(rows);
      setOwnerLeadsTotal(response.data?.total || 0);

      setOwnerLeadDraftStatuses((prev) => {
        const next = { ...prev };
        rows.forEach((lead) => {
          if (!next[lead.id]) next[lead.id] = lead.status || 'new';
        });
        return next;
      });

      setOwnerLeadDraftNotes((prev) => {
        const next = { ...prev };
        rows.forEach((lead) => {
          if (next[lead.id] === undefined) next[lead.id] = lead.admin_note || '';
        });
        return next;
      });

      setOwnerLeadDraftLastContactDates((prev) => {
        const next = { ...prev };
        rows.forEach((lead) => {
          if (next[lead.id] === undefined) {
            const value = lead.last_contact_date ? String(lead.last_contact_date).slice(0, 10) : '';
            next[lead.id] = value;
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch owner leads:', err);
      setOwnerLeadsError('Unable to load owner leads table. Please refresh.');
    } finally {
      setOwnerLeadsLoading(false);
    }
  }, [ownerLeadSearch, ownerLeadSort, ownerLeadStatusFilter]);

  const openMemberProfile = async (member) => {
    setSelectedMember(member);
    setSelectedMemberProfile(null);
    setSelectedMemberActivity(null);
    setSelectedMemberVerificationTimeline([]);
    setVerificationAdminNotes((prev) => ({
      ...prev,
      [member.id]: member.verification_last_note || '',
    }));
    setProfileDialogOpen(true);
    setProfileLoading(true);

    try {
      const response = await axios.get(`/admin/users/${member.id}/profile`);
      setSelectedMember((prev) => ({ ...(prev || {}), ...(response.data?.user || {}) }));
      setSelectedMemberProfile(response.data?.profile || null);
      setSelectedMemberActivity(response.data?.activity || null);
      setSelectedMemberVerificationTimeline(response.data?.verification_timeline || []);
    } catch (err) {
      console.error('Failed to fetch member profile details:', err);
      toast.error('Unable to open member profile details');
    } finally {
      setProfileLoading(false);
    }
  };

  const cleanupDemoMessages = async () => {
    setCleanupLoading(true);
    try {
      const response = await axios.post('/admin/cleanup-demo-messages', {
        remove_test_accounts_messages: true,
        content_markers: [],
      });
      const removed = response.data?.deleted_messages_count || 0;
      toast.success(`Removed ${removed} demo/test messages`);
      await fetchStats();
      await fetchMembers();
    } catch (err) {
      console.error('Demo message cleanup failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to clean demo messages');
    } finally {
      setCleanupLoading(false);
    }
  };

  const runLegacyMigration = async () => {
    const ok = window.confirm('Run legacy data migration now? This is safe and idempotent.');
    if (!ok) return;

    setMigrationRunning(true);
    try {
      const response = await axios.post('/admin/migrate-legacy-records');
      setMigrationResult(response.data?.migration_summary || null);
      toast.success('Legacy migration completed successfully');
      await refreshAdminData();
    } catch (err) {
      console.error('Legacy migration failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to run legacy migration');
    } finally {
      setMigrationRunning(false);
    }
  };

  const requestPermanentDeletePhrase = (label) => {
    const phrase = window.prompt(`Type "delete permanently" to confirm: ${label}`);
    if ((phrase || '').trim().toLowerCase() !== 'delete permanently') {
      toast.error('Confirmation phrase did not match. Delete cancelled.');
      return null;
    }
    return (phrase || '').trim();
  };

  const refreshAdminData = async () => {
    await fetchStats();
    await fetchMembers();
    await fetchAdminJobs();
    await fetchReviewModerationRows();
    await fetchOwnerLeads();
    if (drilldownOpen && drilldownType) {
      await loadDrilldownData(drilldownType, drilldownTimeRange);
    }
  };

  const runBulkCleanup = async ({ key, payload, successLabel, requiresDeletePhrase = false }) => {
    if (cleanupTaskLoadingKey) return;

    let finalPayload = { ...payload };

    if (requiresDeletePhrase || payload.action === 'delete') {
      const phrase = requestPermanentDeletePhrase(successLabel);
      if (!phrase) return;
      finalPayload.confirmation_phrase = phrase;
    } else {
      const ok = window.confirm(`Confirm archive action: ${successLabel}`);
      if (!ok) return;
    }

    setCleanupTaskLoadingKey(key);
    try {
      const response = await axios.post('/admin/cleanup/bulk', finalPayload);
      const affected = response.data?.affected_count ?? response.data?.deleted_counts?.users ?? 0;
      toast.success(`${successLabel} complete${affected ? ` (${affected} affected)` : ''}`);
      await refreshAdminData();
    } catch (err) {
      console.error('Bulk cleanup failed:', err);
      toast.error(err.response?.data?.detail || 'Bulk cleanup failed');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const archiveUser = async (member) => {
    if (!member?.id) return;
    const ok = window.confirm(`Archive user ${member.email}?`);
    if (!ok) return;

    setCleanupTaskLoadingKey(`archive-user-${member.id}`);
    try {
      await axios.put(`/admin/users/${member.id}/archive`);
      toast.success('User archived');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive user');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const deleteUserPermanently = async (member) => {
    if (!member?.id) return;
    const phrase = requestPermanentDeletePhrase(`Delete user ${member.email}`);
    if (!phrase) return;

    setCleanupTaskLoadingKey(`delete-user-${member.id}`);
    try {
      await axios.post(`/admin/users/${member.id}/delete-permanent`, { confirmation_phrase: phrase });
      toast.success('User deleted permanently');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const archiveJob = async (jobId) => {
    const ok = window.confirm('Archive this job post?');
    if (!ok) return;

    setCleanupTaskLoadingKey(`archive-job-${jobId}`);
    try {
      await axios.put(`/admin/jobs/${jobId}/archive`);
      toast.success('Job archived');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive job');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const deleteJobPermanently = async (jobId) => {
    const phrase = requestPermanentDeletePhrase(`Delete job ${jobId}`);
    if (!phrase) return;

    setCleanupTaskLoadingKey(`delete-job-${jobId}`);
    try {
      await axios.post(`/admin/jobs/${jobId}/delete-permanent`, { confirmation_phrase: phrase });
      toast.success('Job deleted permanently');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const archiveApplication = async (applicationId) => {
    const ok = window.confirm('Archive this application?');
    if (!ok) return;

    setCleanupTaskLoadingKey(`archive-app-${applicationId}`);
    try {
      await axios.put(`/admin/applications/${applicationId}/archive`);
      toast.success('Application archived');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive application');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const deleteApplicationPermanently = async (applicationId) => {
    const phrase = requestPermanentDeletePhrase(`Delete application ${applicationId}`);
    if (!phrase) return;

    setCleanupTaskLoadingKey(`delete-app-${applicationId}`);
    try {
      await axios.post(`/admin/applications/${applicationId}/delete-permanent`, { confirmation_phrase: phrase });
      toast.success('Application deleted permanently');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete application');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const updateReviewModerationStatus = async (review, nextStatus) => {
    if (!review?.id) return;

    const moderationNote = (reviewModerationNotes[review.id] || '').trim();
    if (nextStatus === 'rejected' && !moderationNote) {
      toast.error('Moderation note is required to reject a review');
      return;
    }

    const actionLabel = nextStatus === 'approved' ? 'approved' : nextStatus === 'rejected' ? 'rejected' : 'updated to pending';
    const ok = window.confirm(`Confirm ${actionLabel} for this review?`);
    if (!ok) return;

    const loadingKey = `review-status-${review.id}-${nextStatus}`;
    setCleanupTaskLoadingKey(loadingKey);
    try {
      await axios.put(`/admin/reviews/${review.id}/status`, {
        status: nextStatus,
        moderation_note: moderationNote || undefined,
      });
      toast.success(`Review ${actionLabel} successfully`);
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update review status');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const deleteReviewModerationRow = async (review) => {
    if (!review?.id) return;
    const moderationNote = (reviewModerationNotes[review.id] || '').trim();
    if (!moderationNote) {
      toast.error('Moderation note is required to delete a review');
      return;
    }

    const ok = window.confirm('Delete this review permanently? This cannot be undone.');
    if (!ok) return;

    const loadingKey = `review-delete-${review.id}`;
    setCleanupTaskLoadingKey(loadingKey);
    try {
      await axios.delete(`/admin/reviews/${review.id}`, {
        data: {
          moderation_note: moderationNote,
        },
      });
      toast.success('Review deleted permanently');
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete review');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const updateUserVerificationStatus = async (member, nextStatus, action = 'status_update') => {
    if (!member?.id) return;
    const adminNote = (verificationAdminNotes[member.id] || '').trim();
    if (action === 'request_more_info' && !adminNote) {
      toast.error('Please provide an admin note before requesting more information.');
      return;
    }

    const actionLabel = action === 'request_more_info'
      ? 'request more information'
      : nextStatus === 'approved'
        ? 'verify this account'
        : nextStatus === 'rejected'
          ? 'reject this verification'
          : 'set verification back to pending';

    const ok = window.confirm(`Confirm ${actionLabel} for ${member.email || member.id}?`);
    if (!ok) return;

    const loadingKey = action === 'status_update'
      ? `verification-${member.id}-${nextStatus}`
      : `verification-${member.id}-${nextStatus}-${action}`;
    setCleanupTaskLoadingKey(loadingKey);
    try {
      await axios.put(`/admin/users/${member.id}/verification-status`, {
        status: nextStatus,
        action,
        admin_note: adminNote || undefined,
      });

      if (nextStatus === 'approved') {
        toast.success('Verification approved successfully');
      } else if (action === 'request_more_info') {
        toast.success('Requested more information from user');
      } else if (nextStatus === 'rejected') {
        toast.success('Verification rejected successfully');
      } else {
        toast.success('Verification status updated');
      }

      await refreshAdminData();
      if (profileDialogOpen && selectedMember?.id === member.id) {
        await openMemberProfile(member);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update verification status');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const updateOwnerLead = async (lead) => {
    if (!lead?.id) return;

    const nextStatus = (ownerLeadDraftStatuses[lead.id] || lead.status || 'new').trim().toLowerCase();
    const nextNote = (ownerLeadDraftNotes[lead.id] || '').trim();
    const nextLastContactDate = ownerLeadDraftLastContactDates[lead.id] ?? (lead.last_contact_date ? String(lead.last_contact_date).slice(0, 10) : '');

    const payload = {
      status: nextStatus,
      admin_note: nextNote,
      last_contact_date: nextLastContactDate,
    };

    const loadingKey = `owner-lead-save-${lead.id}`;
    setCleanupTaskLoadingKey(loadingKey);
    try {
      await axios.put(`/admin/owner-leads/${lead.id}`, payload);
      toast.success('Owner lead updated');
      await fetchOwnerLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update owner lead');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const cleanupOrphanedFiles = async () => {
    const ok = window.confirm('Cleanup orphaned file references now?');
    if (!ok) return;

    setCleanupTaskLoadingKey('cleanup-orphan-files');
    try {
      const response = await axios.post('/admin/files/cleanup-orphans');
      const removed = response.data?.deleted_counts?.file_references_removed || 0;
      toast.success(`Orphaned file cleanup complete (${removed} references cleaned)`);
      await refreshAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cleanup orphaned files');
    } finally {
      setCleanupTaskLoadingKey('');
    }
  };

  const drilldownConfigs = {
    totalUsers: {
      title: 'All Users',
      fetch: (timeRange) => axios.get('/admin/users', { params: { limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    bartenders: {
      title: 'All Bartenders',
      fetch: (timeRange) => axios.get('/admin/users', { params: { limit: 200, user_type: 'bartender', time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    barOwners: {
      title: 'All Bar Owners',
      fetch: (timeRange) => axios.get('/admin/users', { params: { limit: 200, user_type: 'bar_owner', time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    newToday: {
      title: 'Users Joined Today',
      fetch: () => axios.get('/admin/users', { params: { limit: 200, time_range: 'today' } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    premiumUsers: {
      title: 'Premium Users',
      fetch: (timeRange) => axios.get('/admin/users', { params: { limit: 200, premium_only: true, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    verifiedBartenders: {
      title: 'Verified Bartenders',
      fetch: (timeRange) => axios.get('/admin/verified-users', { params: { role: 'bartender', limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    verifiedBarOwners: {
      title: 'Verified Bar Owners',
      fetch: (timeRange) => axios.get('/admin/verified-users', { params: { role: 'bar_owner', limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.users || [], total: response.data?.total || 0 }),
    },
    totalJobPosts: {
      title: 'All Job Posts',
      fetch: (timeRange) => axios.get('/admin/jobs', { params: { limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.jobs || [], total: response.data?.total || 0 }),
    },
    jobsPostedToday: {
      title: 'Jobs Posted Today',
      fetch: () => axios.get('/admin/jobs', { params: { limit: 200, time_range: 'today' } }),
      parse: (response) => ({ items: response.data?.jobs || [], total: response.data?.total || 0 }),
    },
    totalApplications: {
      title: 'All Applications',
      fetch: (timeRange) => axios.get('/admin/applications', { params: { limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.applications || [], total: response.data?.total || 0 }),
    },
    applicationsToday: {
      title: 'Applications Submitted Today',
      fetch: () => axios.get('/admin/applications', { params: { limit: 200, time_range: 'today' } }),
      parse: (response) => ({ items: response.data?.applications || [], total: response.data?.total || 0 }),
    },
    totalMessages: {
      title: 'All Messages',
      fetch: (timeRange) => axios.get('/admin/messages', { params: { limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.messages || [], total: response.data?.total || 0 }),
    },
    totalReviews: {
      title: 'All Reviews',
      fetch: (timeRange) => axios.get('/admin/reviews', { params: { limit: 200, time_range: timeRange } }),
      parse: (response) => ({ items: response.data?.reviews || [], total: response.data?.total || 0 }),
    },
  };

  const loadDrilldownData = async (type, timeRange = 'all') => {
    const config = drilldownConfigs[type];
    if (!config) return;

    setDrilldownLoading(true);
    try {
      const response = await config.fetch(timeRange);
      const parsed = config.parse(response);
      setDrilldownItems(parsed.items || []);
      setDrilldownTotal(parsed.total || 0);
    } catch (err) {
      console.error('Failed to load drilldown data:', err);
      toast.error('Unable to load details for this card');
    } finally {
      setDrilldownLoading(false);
    }
  };

  const openStatDrilldown = async (type) => {
    const config = drilldownConfigs[type];
    if (!config) return;

    setDrilldownType(type);
    setDrilldownTitle(config.title);
    setDrilldownTimeRange('all');
    setDrilldownItems([]);
    setDrilldownTotal(0);
    setDrilldownOpen(true);
    await loadDrilldownData(type, 'all');
  };

  useEffect(() => {
    fetchStats();
    fetchAdminJobs();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    
    return () => clearInterval(interval);
  }, [fetchStats, fetchAdminJobs]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    fetchReviewModerationRows();
  }, [fetchReviewModerationRows]);

  useEffect(() => {
    fetchOwnerLeads();
  }, [fetchOwnerLeads]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  const isInstantShiftRequest = useCallback((job) => {
    if (!job) return false;

    if (job.instant_shift_fill === true || job.source === 'instant_shift_fill' || job.created_via === 'instant_shift_fill') {
      return true;
    }

    const marker = `${job.title || ''} ${job.description || ''}`.toLowerCase();
    return marker.includes('instant shift') || marker.includes('shift fill') || marker.includes('urgent shift') || marker.includes('coverage');
  }, []);

  const sortByDate = useCallback((items, sortMode) => {
    const mapped = [...items];
    mapped.sort((a, b) => {
      const aDate = new Date(a.created_at || a.start_date || 0).getTime();
      const bDate = new Date(b.created_at || b.start_date || 0).getTime();
      return sortMode === 'oldest' ? aDate - bDate : bDate - aDate;
    });
    return mapped;
  }, []);

  const sortByName = useCallback((items, sortMode) => {
    if (sortMode !== 'name_asc') return sortByDate(items, sortMode);

    const mapped = [...items];
    mapped.sort((a, b) => {
      const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
      const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      return aName.localeCompare(bName);
    });
    return mapped;
  }, [sortByDate]);

  const canManageVerification = useCallback((member) => {
    const role = (member?.user_type || '').toLowerCase();
    return ['bartender', 'bartenders', 'bar_tender', 'bar-tender', 'bar_owner', 'owner', 'bar-owner', 'bar owner', 'bar_owners'].includes(role);
  }, []);

  const visibleMembers = useMemo(() => {
    const filtered = members.filter((member) => {
      const roleOk = memberRoleFilter === 'all' ? true : member.user_type === memberRoleFilter;
      const term = memberSearch.trim().toLowerCase();
      const termOk = !term
        ? true
        : `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase().includes(term);
      return roleOk && termOk;
    });

    return sortByName(filtered, memberSort);
  }, [members, memberSort, memberRoleFilter, memberSearch, sortByName]);

  const bartenderRows = useMemo(() => {
    const filtered = members.filter((member) => member.user_type === 'bartender').filter((member) => {
      const term = bartendersSearch.trim().toLowerCase();
      if (!term) return true;
      const blob = `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase();
      return blob.includes(term);
    });
    return sortByName(filtered, bartendersSort);
  }, [members, bartendersSearch, bartendersSort, sortByName]);

  const ownerRows = useMemo(() => {
    const filtered = members.filter((member) => member.user_type === 'bar_owner').filter((member) => {
      const term = ownersSearch.trim().toLowerCase();
      if (!term) return true;
      const blob = `${member.first_name || ''} ${member.last_name || ''} ${member.email || ''}`.toLowerCase();
      return blob.includes(term);
    });
    return sortByName(filtered, ownersSort);
  }, [members, ownersSearch, ownersSort, sortByName]);

  const jobsRows = useMemo(() => {
    const filtered = adminJobs.filter((job) => {
      const term = jobsSearch.trim().toLowerCase();
      const statusOk = jobsStatusFilter === 'all' ? true : jobsStatusFilter === 'active' ? !!job.is_active : !job.is_active;
      const termOk = !term
        ? true
        : `${job.title || ''} ${job.description || ''} ${job.location?.city || ''} ${job.bar_owner?.email || ''}`.toLowerCase().includes(term);
      return statusOk && termOk;
    });

    return sortByDate(filtered, jobsSort);
  }, [adminJobs, jobsSearch, jobsStatusFilter, jobsSort, sortByDate]);

  const instantShiftRows = useMemo(() => {
    const filtered = adminJobs.filter((job) => isInstantShiftRequest(job)).filter((job) => {
      const statusOk = instantStatusFilter === 'all' ? true : instantStatusFilter === 'active' ? !!job.is_active : !job.is_active;
      const term = instantSearch.trim().toLowerCase();
      const termOk = !term
        ? true
        : `${job.title || ''} ${job.description || ''} ${job.location?.city || ''}`.toLowerCase().includes(term);
      return statusOk && termOk;
    });

    return sortByDate(filtered, instantSort);
  }, [adminJobs, isInstantShiftRequest, instantSearch, instantStatusFilter, instantSort, sortByDate]);

  const visibleReviewModerationRows = useMemo(() => {
    const term = reviewModerationSearch.trim().toLowerCase();
    const filtered = reviewModerationRows.filter((review) => {
      if (!term) return true;
      const blob = `${review.comment || ''} ${review.bartender?.name || ''} ${review.bartender?.email || ''} ${review.bar_owner?.name || ''} ${review.bar_owner?.email || ''}`.toLowerCase();
      return blob.includes(term);
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return reviewModerationSort === 'oldest' ? aDate - bDate : bDate - aDate;
    });
    return sorted;
  }, [reviewModerationRows, reviewModerationSearch, reviewModerationSort]);

  const getReviewHistoryEntries = useCallback((review) => {
    const history = Array.isArray(review?.moderation_history) ? [...review.moderation_history] : [];
    history.sort((a, b) => new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime());
    return history;
  }, []);

  const exportMembersCsv = useCallback(() => {
    const rows = visibleMembers.map((member) => ({
      full_name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
      email: member.email || '',
      role: (member.user_type || '').replace(/_/g, ' '),
      is_admin: member.is_admin ? 'Yes' : 'No',
      is_premium: member.is_premium ? 'Yes' : 'No',
      verification_status: member.verification_status || 'pending',
      account_status: member.is_active ? 'Active' : 'Inactive',
      joined_at: member.created_at || '',
    }));

    exportRowsToCsv('admin-members', [
      { key: 'full_name', label: 'Full Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'is_admin', label: 'Admin' },
      { key: 'is_premium', label: 'Premium' },
      { key: 'verification_status', label: 'Verification Status' },
      { key: 'account_status', label: 'Account Status' },
      { key: 'joined_at', label: 'Joined At' },
    ], rows);
  }, [visibleMembers, exportRowsToCsv]);

  const exportJobsCsv = useCallback(() => {
    const rows = jobsRows.map((job) => ({
      title: job.title || '',
      owner_email: job.bar_owner?.email || '',
      city: job.location?.city || '',
      state: job.location?.state || '',
      hourly_rate: job.hourly_rate || 0,
      status: job.is_active ? 'Active' : 'Closed',
      applications_count: job.applications_count || 0,
      invited_count: job.invited_count || 0,
      created_at: job.created_at || '',
    }));

    exportRowsToCsv('admin-jobs', [
      { key: 'title', label: 'Job Title' },
      { key: 'owner_email', label: 'Owner Email' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'hourly_rate', label: 'Hourly Rate' },
      { key: 'status', label: 'Status' },
      { key: 'applications_count', label: 'Applications' },
      { key: 'invited_count', label: 'Invited Count' },
      { key: 'created_at', label: 'Created At' },
    ], rows);
  }, [jobsRows, exportRowsToCsv]);

  const exportReviewsCsv = useCallback(() => {
    const rows = visibleReviewModerationRows.map((review) => ({
      date: review.created_at || '',
      reviewer_name: review.bar_owner?.name || '',
      reviewer_email: review.bar_owner?.email || '',
      bartender_name: review.bartender?.name || '',
      bartender_email: review.bartender?.email || '',
      rating: review.rating || 0,
      status: review.status || 'pending',
      comment: review.comment || '',
      flagged_reason: review.flagged_reason || '',
      moderation_note: review.moderation_note || '',
      moderated_at: review.moderated_at || '',
    }));

    exportRowsToCsv('admin-reviews', [
      { key: 'date', label: 'Date' },
      { key: 'reviewer_name', label: 'Reviewer Name' },
      { key: 'reviewer_email', label: 'Reviewer Email' },
      { key: 'bartender_name', label: 'Bartender Name' },
      { key: 'bartender_email', label: 'Bartender Email' },
      { key: 'rating', label: 'Rating' },
      { key: 'status', label: 'Status' },
      { key: 'comment', label: 'Comment' },
      { key: 'flagged_reason', label: 'Flagged Reason' },
      { key: 'moderation_note', label: 'Moderation Note' },
      { key: 'moderated_at', label: 'Moderated At' },
    ], rows);
  }, [visibleReviewModerationRows, exportRowsToCsv]);

  const exportOwnerLeadsCsv = useCallback(() => {
    const rows = ownerLeads.map((lead) => ({
      created_at: lead.created_at || '',
      updated_at: lead.updated_at || '',
      full_name: lead.full_name || '',
      email: lead.email || '',
      source: lead.source || '',
      status: lead.status || 'new',
      last_contact_date: lead.last_contact_date || '',
      admin_note: lead.admin_note || '',
      updated_by_admin_email: lead.updated_by_admin_email || '',
    }));

    exportRowsToCsv('admin-owner-leads', [
      { key: 'created_at', label: 'Created At' },
      { key: 'updated_at', label: 'Updated At' },
      { key: 'full_name', label: 'Full Name' },
      { key: 'email', label: 'Email' },
      { key: 'source', label: 'Source' },
      { key: 'status', label: 'Status' },
      { key: 'last_contact_date', label: 'Last Contact Date' },
      { key: 'admin_note', label: 'Admin Note' },
      { key: 'updated_by_admin_email', label: 'Updated By' },
    ], rows);
  }, [ownerLeads, exportRowsToCsv]);

  const activeJobsCount = useMemo(() => adminJobs.filter((job) => !!job.is_active).length, [adminJobs]);
  const barsBusinessProfilesCount = useMemo(() => members.filter((member) => member.user_type === 'bar_owner').length, [members]);

  const ownerLeadPipeline = useMemo(() => {
    const counts = {
      new: 0,
      contacted: 0,
      qualified: 0,
      converted: 0,
      archived: 0,
    };

    ownerLeads.forEach((lead) => {
      const status = (lead.status || 'new').toLowerCase();
      if (counts[status] !== undefined) {
        counts[status] += 1;
      }
    });

    return counts;
  }, [ownerLeads]);

  const userDrilldownTypes = ['totalUsers', 'bartenders', 'barOwners', 'newToday', 'premiumUsers'];

  const openProfileFromDrilldown = (item) => {
    if (!item) return;

    if (item.user?.id) {
      openMemberProfile({
        id: item.user.id,
        first_name: item.user.first_name || '',
        last_name: item.user.last_name || '',
        email: item.user.email || '',
        user_type: item.user.user_type || '',
        created_at: item.created_at,
      });
      return;
    }

    if (item.id) {
      openMemberProfile(item);
    }
  };

  const openJobDetails = (job) => {
    if (!job) return;
    setDrilldownType('jobDetails');
    setDrilldownTitle(`Job Details: ${job.title || 'Untitled Job'}`);
    setDrilldownTimeRange('all');
    setDrilldownItems([job]);
    setDrilldownTotal(1);
    setDrilldownOpen(true);
  };

  // Check if user is admin - render 403 if not
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-white/5 backdrop-blur-md border border-red-500/20">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-red-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-6">
              You don&apos;t have permission to access this admin area.
            </p>
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">
                <strong>403 Forbidden</strong><br />
                Admin access required
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white/5 backdrop-blur-md border border-red-500/20">
          <CardContent className="p-8 text-center">
            <p className="text-red-400">{error}</p>
            <button
              onClick={fetchStats}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-[hsl(var(--primary-light))]"
            >
              Retry
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2 font-['Barlow_Condensed'] uppercase">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.first_name}! Here&apos;s what&apos;s happening with BarsConnect.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Last updated</p>
          <p className="text-sm text-white">{lastUpdated || 'Loading...'}</p>
          <Button
            onClick={() => {
              fetchStats();
              fetchMembers();
              fetchAdminJobs();
              fetchReviewModerationRows();
              fetchOwnerLeads();
            }}
            variant="outline"
            size="sm"
            className="mt-2 border-primary/30 text-primary hover:bg-primary/10"
            data-testid="admin-refresh-dashboard-btn"
          >
            Refresh Data
          </Button>
          <Button
            onClick={cleanupDemoMessages}
            variant="outline"
            size="sm"
            className="mt-2 border-primary/30 text-primary hover:bg-primary/10 ml-2"
            data-testid="admin-cleanup-demo-messages-btn"
            disabled={cleanupLoading}
          >
            {cleanupLoading ? 'Cleaning...' : 'Clean Demo Messages'}
          </Button>
          <Button
            onClick={runLegacyMigration}
            variant="outline"
            size="sm"
            className="mt-2 border-primary/30 text-primary hover:bg-primary/10 ml-2"
            data-testid="admin-run-legacy-migration-btn"
            disabled={migrationRunning}
          >
            {migrationRunning ? 'Running Migration...' : 'Run Legacy Migration'}
          </Button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Business Oversight KPI Section */}
          <div className="mb-8" data-testid="admin-business-oversight-kpis-section">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Core Platform Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Bartenders" value={stats?.users?.bartenders || bartenderRows.length || 0} icon={Users} loading={loading} onClick={() => openStatDrilldown('bartenders')} testId="admin-kpi-total-bartenders-card" />
              <StatCard label="Total Owners" value={stats?.users?.bar_owners || ownerRows.length || 0} icon={Building2} loading={loading} onClick={() => openStatDrilldown('barOwners')} testId="admin-kpi-total-owners-card" />
              <StatCard label="Active Jobs" value={stats?.jobs?.active ?? activeJobsCount} icon={Briefcase} loading={loading || adminJobsLoading} onClick={() => openStatDrilldown('totalJobPosts')} testId="admin-kpi-active-jobs-card" />
              <StatCard label="Jobs Filled" value={stats?.jobs?.filled ?? 0} icon={CheckCircle} loading={loading} testId="admin-kpi-jobs-filled-card" />
              <StatCard label="Messages" value={stats?.engagement?.total_messages || 0} icon={MessageSquare} loading={loading} onClick={() => openStatDrilldown('totalMessages')} testId="admin-kpi-messages-card" />
              <StatCard label="New Signups" value={stats?.users?.new_signups ?? stats?.users?.new_today ?? 0} icon={TrendingUp} loading={loading} onClick={() => openStatDrilldown('newToday')} testId="admin-kpi-new-signups-card" />
              <StatCard label="Pending Verifications" value={stats?.users?.pending_verifications ?? 0} icon={Shield} loading={loading} testId="admin-kpi-pending-verifications-card" />
            </div>
          </div>

          <div className="mb-8" data-testid="admin-verification-queue-section">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Verification Queue
            </h2>
            <Card className="bg-white/5 backdrop-blur-md border border-white/10" data-testid="admin-verification-queue-card">
              <CardContent className="p-4">
                {visibleMembers.filter((member) => canManageVerification(member) && (member.verification_status || 'pending') === 'pending').length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="admin-verification-queue-empty">No pending verifications right now.</p>
                ) : (
                  <div className="space-y-2" data-testid="admin-verification-queue-list">
                    {visibleMembers
                      .filter((member) => canManageVerification(member) && (member.verification_status || 'pending') === 'pending')
                      .slice(0, 12)
                      .map((member) => (
                        <div key={member.id} className="flex items-center justify-between rounded border border-white/10 px-3 py-2" data-testid={`admin-verification-queue-row-${member.id}`}>
                          <div>
                            <p className="text-sm text-white" data-testid={`admin-verification-queue-name-${member.id}`}>{member.first_name} {member.last_name}</p>
                            <p className="text-xs text-muted-foreground" data-testid={`admin-verification-queue-email-${member.id}`}>{member.email}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => openMemberProfile(member)} data-testid={`admin-verification-queue-review-btn-${member.id}`}>
                            Review
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-8" data-testid="admin-owner-leads-panel-card">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <CardTitle className="text-xl font-semibold text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Owner Leads Pipeline ({ownerLeadsTotal})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={exportOwnerLeadsCsv} data-testid="admin-owner-leads-export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              <CardDescription data-testid="admin-owner-leads-description">
                Track owner lead status, keep internal notes, and move leads through the conversion pipeline.
              </CardDescription>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="admin-owner-leads-pipeline-summary">
                <Badge variant="outline" className="justify-center" data-testid="admin-owner-leads-count-new">New: {ownerLeadPipeline.new}</Badge>
                <Badge variant="outline" className="justify-center" data-testid="admin-owner-leads-count-contacted">Contacted: {ownerLeadPipeline.contacted}</Badge>
                <Badge variant="outline" className="justify-center" data-testid="admin-owner-leads-count-qualified">Qualified: {ownerLeadPipeline.qualified}</Badge>
                <Badge variant="outline" className="justify-center" data-testid="admin-owner-leads-count-converted">Converted: {ownerLeadPipeline.converted}</Badge>
                <Badge variant="outline" className="justify-center" data-testid="admin-owner-leads-count-archived">Archived: {ownerLeadPipeline.archived}</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                <Input
                  placeholder="Search owner leads by name/email/source"
                  value={ownerLeadSearch}
                  onChange={(event) => setOwnerLeadSearch(event.target.value)}
                  data-testid="admin-owner-leads-search-input"
                />
                <Select value={ownerLeadStatusFilter} onValueChange={setOwnerLeadStatusFilter}>
                  <SelectTrigger data-testid="admin-owner-leads-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ownerLeadSort} onValueChange={setOwnerLeadSort}>
                  <SelectTrigger data-testid="admin-owner-leads-sort-select">
                    <SelectValue placeholder="Sort leads" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {ownerLeadsLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-owner-leads-loading-state">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : ownerLeadsError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-owner-leads-error-state">{ownerLeadsError}</div>
              ) : ownerLeads.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-owner-leads-empty-state">No owner leads found for current filters.</div>
              ) : (
                <div className="space-y-3" data-testid="admin-owner-leads-table-wrapper">
                  {ownerLeads.map((lead) => (
                    <div key={lead.id} className="rounded-lg border border-white/10 p-4 bg-white/5" data-testid={`admin-owner-lead-row-${lead.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div>
                          <p className="text-white font-medium" data-testid={`admin-owner-lead-name-${lead.id}`}>{lead.full_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground" data-testid={`admin-owner-lead-email-${lead.id}`}>{lead.email || 'N/A'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" data-testid={`admin-owner-lead-status-${lead.id}`}>{lead.status || 'new'}</Badge>
                          <span className="text-xs text-muted-foreground" data-testid={`admin-owner-lead-source-${lead.id}`}>Source: {lead.source || 'owner_landing_page'}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <label className="text-xs text-muted-foreground" htmlFor={`owner-lead-status-${lead.id}`}>Pipeline Status</label>
                          <Select
                            value={ownerLeadDraftStatuses[lead.id] || lead.status || 'new'}
                            onValueChange={(value) => setOwnerLeadDraftStatuses((prev) => ({ ...prev, [lead.id]: value }))}
                          >
                            <SelectTrigger id={`owner-lead-status-${lead.id}`} data-testid={`admin-owner-lead-status-select-${lead.id}`}>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="qualified">Qualified</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground" htmlFor={`owner-lead-last-contact-${lead.id}`}>Last Contact Date (Admin Only)</label>
                          <Input
                            id={`owner-lead-last-contact-${lead.id}`}
                            type="date"
                            value={ownerLeadDraftLastContactDates[lead.id] ?? (lead.last_contact_date ? String(lead.last_contact_date).slice(0, 10) : '')}
                            onChange={(event) => setOwnerLeadDraftLastContactDates((prev) => ({ ...prev, [lead.id]: event.target.value }))}
                            data-testid={`admin-owner-lead-last-contact-input-${lead.id}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs text-muted-foreground" htmlFor={`owner-lead-note-${lead.id}`}>Internal Note</label>
                          <Textarea
                            id={`owner-lead-note-${lead.id}`}
                            value={ownerLeadDraftNotes[lead.id] ?? lead.admin_note ?? ''}
                            onChange={(event) => setOwnerLeadDraftNotes((prev) => ({ ...prev, [lead.id]: event.target.value }))}
                            placeholder="Add outreach context, objections, or next step"
                            className="min-h-[72px]"
                            data-testid={`admin-owner-lead-note-input-${lead.id}`}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground" data-testid={`admin-owner-lead-updated-${lead.id}`}>
                          Updated: {formatDateTime(lead.updated_at)}
                        </p>
                        <p className="text-xs text-muted-foreground" data-testid={`admin-owner-lead-last-contact-display-${lead.id}`}>
                          Last Contact: {formatDateTime(lead.last_contact_date)}
                        </p>
                        <Button
                          size="sm"
                          onClick={() => updateOwnerLead(lead)}
                          disabled={cleanupTaskLoadingKey === `owner-lead-save-${lead.id}`}
                          data-testid={`admin-owner-lead-save-btn-${lead.id}`}
                        >
                          {cleanupTaskLoadingKey === `owner-lead-save-${lead.id}` ? 'Saving...' : 'Save Lead Update'}
                        </Button>
                      </div>

                      <details className="mt-3 rounded-md border border-white/10 bg-black/20 px-3 py-2" data-testid={`admin-owner-lead-note-history-collapsible-${lead.id}`}>
                        <summary className="cursor-pointer text-sm text-white font-medium" data-testid={`admin-owner-lead-note-history-toggle-${lead.id}`}>
                          Note History ({Array.isArray(lead.note_history) ? lead.note_history.length : 0})
                        </summary>
                        <div className="mt-2 space-y-2" data-testid={`admin-owner-lead-note-history-list-${lead.id}`}>
                          {!Array.isArray(lead.note_history) || lead.note_history.length === 0 ? (
                            <p className="text-xs text-muted-foreground" data-testid={`admin-owner-lead-note-history-empty-${lead.id}`}>No note history yet.</p>
                          ) : (
                            [...lead.note_history]
                              .sort((a, b) => new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime())
                              .map((entry) => (
                                <div key={entry.id || `${entry.timestamp}-${entry.admin_email}`} className="rounded border border-white/10 p-2" data-testid={`admin-owner-lead-note-history-item-${lead.id}-${entry.id || entry.timestamp}`}>
                                  <p className="text-xs text-white">{entry.admin_email || 'Unknown admin'} • {formatDateTime(entry.timestamp)}</p>
                                  <p className="text-xs text-muted-foreground">{entry.previous_status || 'new'} → {entry.new_status || 'new'}</p>
                                  <p className="text-xs text-muted-foreground">{entry.note || '(No note)'}</p>
                                </div>
                              ))
                          )}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Users
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Users"
                value={stats?.users?.total || 0}
                icon={Users}
                loading={loading}
                onClick={() => openStatDrilldown('totalUsers')}
                testId="admin-stat-total-users-card"
              />
              <StatCard
                label="Bartenders"
                value={stats?.users?.bartenders || 0}
                icon={Users}
                loading={loading}
                onClick={() => openStatDrilldown('bartenders')}
                testId="admin-stat-bartenders-card"
              />
              <StatCard
                label="Bar Owners"
                value={stats?.users?.bar_owners || 0}
                icon={Briefcase}
                loading={loading}
                onClick={() => openStatDrilldown('barOwners')}
                testId="admin-stat-bar-owners-card"
              />
              <StatCard
                label="New Today"
                value={stats?.users?.new_today || 0}
                icon={TrendingUp}
                trend={stats?.users?.new_today > 0 ? 'Active' : null}
                loading={loading}
                onClick={() => openStatDrilldown('newToday')}
                testId="admin-stat-new-today-card"
              />
            </div>
          </div>

          {/* Premium & Verified */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              Premium & Verified
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label="Premium Users"
                value={stats?.users?.premium || 0}
                icon={Crown}
                loading={loading}
                onClick={() => openStatDrilldown('premiumUsers')}
                testId="admin-stat-premium-users-card"
              />
              <StatCard
                label="Verified Bartenders"
                value={stats?.users?.verified_bartenders || 0}
                icon={Shield}
                loading={loading}
                onClick={() => openStatDrilldown('verifiedBartenders')}
                testId="admin-stat-verified-bartenders-card"
              />
              <StatCard
                label="Verified Bar Owners"
                value={stats?.users?.verified_bar_owners || 0}
                icon={Shield}
                loading={loading}
                onClick={() => openStatDrilldown('verifiedBarOwners')}
                testId="admin-stat-verified-bar-owners-card"
              />
            </div>
          </div>

          {/* Jobs & Applications */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              Jobs & Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Job Posts"
                value={stats?.jobs?.total || 0}
                icon={Briefcase}
                loading={loading}
                onClick={() => openStatDrilldown('totalJobPosts')}
                testId="admin-stat-total-job-posts-card"
              />
              <StatCard
                label="Jobs Posted Today"
                value={stats?.jobs?.posted_today || 0}
                icon={TrendingUp}
                loading={loading}
                onClick={() => openStatDrilldown('jobsPostedToday')}
                testId="admin-stat-jobs-posted-today-card"
              />
              <StatCard
                label="Total Applications"
                value={stats?.applications?.total || 0}
                icon={FileText}
                loading={loading}
                onClick={() => openStatDrilldown('totalApplications')}
                testId="admin-stat-total-applications-card"
              />
              <StatCard
                label="Applications Today"
                value={stats?.applications?.submitted_today || 0}
                icon={TrendingUp}
                loading={loading}
                onClick={() => openStatDrilldown('applicationsToday')}
                testId="admin-stat-applications-today-card"
              />
            </div>
          </div>

          {/* Engagement */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Engagement
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard
                label="Total Messages"
                value={stats?.engagement?.total_messages || 0}
                icon={MessageSquare}
                loading={loading}
                onClick={() => openStatDrilldown('totalMessages')}
                testId="admin-stat-total-messages-card"
              />
              <StatCard
                label="Total Reviews"
                value={stats?.engagement?.total_reviews || 0}
                icon={Star}
                loading={loading}
                onClick={() => openStatDrilldown('totalReviews')}
                testId="admin-stat-total-reviews-card"
              />
            </div>
          </div>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-8" data-testid="admin-review-moderation-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Review Moderation
              </CardTitle>
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={exportReviewsCsv} data-testid="admin-review-moderation-export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              <CardDescription data-testid="admin-review-moderation-description">
                Approve, reject, or delete reviews. Public-facing pages only show approved reviews.
              </CardDescription>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                <Input
                  placeholder="Search reviewer, bartender, or comment"
                  value={reviewModerationSearch}
                  onChange={(event) => setReviewModerationSearch(event.target.value)}
                  data-testid="admin-review-moderation-search-input"
                />
                <Select value={reviewModerationStatusFilter} onValueChange={setReviewModerationStatusFilter}>
                  <SelectTrigger data-testid="admin-review-moderation-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="flagged">Flagged</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={reviewModerationSort} onValueChange={setReviewModerationSort}>
                  <SelectTrigger data-testid="admin-review-moderation-sort-select">
                    <SelectValue placeholder="Sort reviews" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {reviewModerationLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-review-moderation-loading-state">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : reviewModerationError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-review-moderation-error-state">{reviewModerationError}</div>
              ) : visibleReviewModerationRows.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-review-moderation-empty-state">No reviews found for current moderation filters.</div>
              ) : (
                <div className="space-y-3" data-testid="admin-review-moderation-table-wrapper">
                  {visibleReviewModerationRows.map((review) => {
                    const isApproved = review.status === 'approved';
                    const isRejected = review.status === 'rejected';
                    const statusClasses = isApproved
                      ? 'border-emerald-500/30 text-emerald-300'
                      : isRejected
                        ? 'border-red-500/30 text-red-300'
                        : 'border-yellow-500/30 text-yellow-300';
                    const moderationHistoryEntries = getReviewHistoryEntries(review);

                    return (
                      <div key={review.id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`admin-review-moderation-row-${review.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div>
                            <p className="text-white font-medium" data-testid={`admin-review-owner-${review.id}`}>
                              {review.bar_owner?.name || 'Unknown'} → {review.bartender?.name || 'Unknown'}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid={`admin-review-created-${review.id}`}>
                              {formatDateTime(review.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={statusClasses} data-testid={`admin-review-status-${review.id}`}>
                              {review.status || 'pending'}
                            </Badge>
                            <Badge variant="outline" className="border-white/20 text-muted-foreground" data-testid={`admin-review-rating-${review.id}`}>
                              {review.rating || 0}/5
                            </Badge>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-3" data-testid={`admin-review-comment-${review.id}`}>
                          {review.comment || '(No comment)'}
                        </p>

                        <div className="space-y-2 mb-3">
                          <label className="text-xs text-muted-foreground" htmlFor={`moderation-note-${review.id}`}>
                            Moderation Note (required for Reject/Delete, optional for Approve)
                          </label>
                          <Textarea
                            id={`moderation-note-${review.id}`}
                            value={reviewModerationNotes[review.id] || ''}
                            onChange={(event) => setReviewModerationNotes((prev) => ({ ...prev, [review.id]: event.target.value }))}
                            placeholder="Add moderation note for this action"
                            className="min-h-[72px]"
                            data-testid={`admin-review-note-input-${review.id}`}
                          />
                        </div>

                        <div className="flex flex-wrap gap-1 mb-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateReviewModerationStatus(review, 'approved')}
                            disabled={cleanupTaskLoadingKey === `review-status-${review.id}-approved` || review.status === 'approved'}
                            data-testid={`admin-review-approve-${review.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateReviewModerationStatus(review, 'rejected')}
                            disabled={cleanupTaskLoadingKey === `review-status-${review.id}-rejected` || review.status === 'rejected'}
                            data-testid={`admin-review-reject-${review.id}`}
                          >
                            <XCircle className="h-3 w-3 mr-1" />Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteReviewModerationRow(review)}
                            disabled={cleanupTaskLoadingKey === `review-delete-${review.id}`}
                            data-testid={`admin-review-delete-${review.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />Delete
                          </Button>
                        </div>

                        <details className="rounded-md border border-white/10 bg-black/20 px-3 py-2" data-testid={`admin-review-history-collapsible-${review.id}`}>
                          <summary className="cursor-pointer text-sm text-white font-medium" data-testid={`admin-review-history-toggle-${review.id}`}>
                            Moderation History ({moderationHistoryEntries.length})
                          </summary>
                          <div className="mt-3 space-y-2" data-testid={`admin-review-history-panel-${review.id}`}>
                            {moderationHistoryEntries.length === 0 ? (
                              <p className="text-xs text-muted-foreground" data-testid={`admin-review-history-empty-${review.id}`}>
                                No moderation actions yet.
                              </p>
                            ) : moderationHistoryEntries.map((entry) => (
                              <div key={entry.id || `${entry.timestamp}-${entry.action_type}`} className="rounded border border-white/10 p-2" data-testid={`admin-review-history-item-${review.id}-${entry.id || entry.timestamp}`}>
                                <p className="text-xs text-white">
                                  <span className="font-medium capitalize">{entry.action_type || 'updated'}</span>
                                  {' • '}
                                  {entry.admin_email || 'Unknown admin'}
                                  {' • '}
                                  {formatDateTime(entry.timestamp)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {entry.previous_status || 'unknown'} → {entry.new_status || 'unknown'}
                                </p>
                                <p className="text-xs text-muted-foreground">{entry.moderation_note || '(No moderation note)'}</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Admin Cleanup Controls */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-8" data-testid="admin-cleanup-controls-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white flex items-center gap-2">
                <Wrench className="h-5 w-5 text-primary" />
                Admin Cleanup Controls
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Archive or permanently delete demo/test/duplicate records. Permanent delete requires confirmation phrase.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {migrationResult && (
                <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="admin-legacy-migration-result">
                  <p className="text-sm font-medium text-white mb-1">Latest Migration Summary</p>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">{JSON.stringify(migrationResult, null, 2)}</pre>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-white mb-2">Users</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-demo-bartenders', payload: { target: 'users', scope: 'demo', user_type: 'bartender', action: 'archive' }, successLabel: 'Archive demo bartenders' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-demo-bartenders-btn">Archive Demo Bartenders</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-demo-bartenders', payload: { target: 'users', scope: 'demo', user_type: 'bartender', action: 'delete' }, successLabel: 'Delete demo bartenders', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-demo-bartenders-btn">Delete Demo Bartenders</Button>
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-dup-bartenders', payload: { target: 'users', scope: 'duplicates', user_type: 'bartender', action: 'archive' }, successLabel: 'Archive duplicate bartenders' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-duplicate-bartenders-btn">Archive Duplicate Bartenders</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-dup-bartenders', payload: { target: 'users', scope: 'duplicates', user_type: 'bartender', action: 'delete' }, successLabel: 'Delete duplicate bartenders', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-duplicate-bartenders-btn">Delete Duplicate Bartenders</Button>

                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-demo-owners', payload: { target: 'users', scope: 'demo', user_type: 'bar_owner', action: 'archive' }, successLabel: 'Archive demo owners' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-demo-owners-btn">Archive Demo Owners</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-demo-owners', payload: { target: 'users', scope: 'demo', user_type: 'bar_owner', action: 'delete' }, successLabel: 'Delete demo owners', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-demo-owners-btn">Delete Demo Owners</Button>
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-dup-owners', payload: { target: 'users', scope: 'duplicates', user_type: 'bar_owner', action: 'archive' }, successLabel: 'Archive duplicate owners' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-duplicate-owners-btn">Archive Duplicate Owners</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-dup-owners', payload: { target: 'users', scope: 'duplicates', user_type: 'bar_owner', action: 'delete' }, successLabel: 'Delete duplicate owners', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-duplicate-owners-btn">Delete Duplicate Owners</Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-white mb-2">Jobs & Applications</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-demo-jobs', payload: { target: 'jobs', scope: 'demo', action: 'archive' }, successLabel: 'Archive demo jobs' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-demo-jobs-btn">Archive Demo Jobs</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-demo-jobs', payload: { target: 'jobs', scope: 'demo', action: 'delete' }, successLabel: 'Delete demo jobs', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-demo-jobs-btn">Delete Demo Jobs</Button>
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-dup-jobs', payload: { target: 'jobs', scope: 'duplicates', action: 'archive' }, successLabel: 'Archive duplicate jobs' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-duplicate-jobs-btn">Archive Duplicate Jobs</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-dup-jobs', payload: { target: 'jobs', scope: 'duplicates', action: 'delete' }, successLabel: 'Delete duplicate jobs', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-duplicate-jobs-btn">Delete Duplicate Jobs</Button>

                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-demo-apps', payload: { target: 'applications', scope: 'demo', action: 'archive' }, successLabel: 'Archive demo applications' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-demo-applications-btn">Archive Demo Applications</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-demo-apps', payload: { target: 'applications', scope: 'demo', action: 'delete' }, successLabel: 'Delete demo applications', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-demo-applications-btn">Delete Demo Applications</Button>
                  <Button size="sm" variant="outline" onClick={() => runBulkCleanup({ key: 'archive-dup-apps', payload: { target: 'applications', scope: 'duplicates', action: 'archive' }, successLabel: 'Archive duplicate applications' })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-archive-duplicate-applications-btn">Archive Duplicate Applications</Button>
                  <Button size="sm" variant="destructive" onClick={() => runBulkCleanup({ key: 'delete-dup-apps', payload: { target: 'applications', scope: 'duplicates', action: 'delete' }, successLabel: 'Delete duplicate applications', requiresDeletePhrase: true })} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-delete-duplicate-applications-btn">Delete Duplicate Applications</Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-white mb-2">Files / References</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={cleanupOrphanedFiles} disabled={!!cleanupTaskLoadingKey} data-testid="cleanup-orphaned-files-btn">
                    Cleanup Orphaned File References
                  </Button>
                  <Button size="sm" variant="outline" onClick={cleanupDemoMessages} disabled={cleanupLoading || !!cleanupTaskLoadingKey} data-testid="cleanup-demo-messages-secondary-btn">
                    {cleanupLoading ? 'Cleaning Demo Messages...' : 'Clean Demo/Test Messages'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Note */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground text-center">
                💡 Stats refresh automatically every 30 seconds. 
                All times are in UTC. Data shown is live from your MongoDB database.
              </p>
            </CardContent>
          </Card>

          {/* Members Report */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mt-8" data-testid="admin-members-report-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Members Report ({membersTotal})
              </CardTitle>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={exportMembersCsv} data-testid="admin-members-export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                <Input
                  placeholder="Search by name or email"
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                  data-testid="admin-members-search-input"
                />
                <Select value={memberRoleFilter} onValueChange={setMemberRoleFilter}>
                  <SelectTrigger data-testid="admin-members-role-filter">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="bar_owner">Bar Owners</SelectItem>
                    <SelectItem value="bartender">Bartenders</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={memberSort} onValueChange={setMemberSort}>
                  <SelectTrigger data-testid="admin-members-sort-select">
                    <SelectValue placeholder="Sort members" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="name_asc">Name A-Z</SelectItem>
                  </SelectContent>
                </Select>

                <div className="text-sm text-muted-foreground flex items-center" data-testid="admin-members-total-info">
                  Showing {visibleMembers.length} of {membersTotal}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {membersLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-members-loading-state">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : membersError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-members-error-state">{membersError}</div>
              ) : visibleMembers.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-members-empty-state">
                  No members found for current filters.
                </div>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-members-table-wrapper">
                  <table className="w-full text-sm" data-testid="admin-members-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-muted-foreground">
                        <th className="py-2 pr-3">Name</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Role</th>
                        <th className="py-2 pr-3">Admin</th>
                        <th className="py-2 pr-3">Premium</th>
                        <th className="py-2 pr-3">Verified</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2">Joined</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleMembers.map((member) => (
                        <tr key={member.id} className="border-b border-white/5" data-testid={`admin-member-row-${member.id}`}>
                          <td className="py-2 pr-3 text-white" data-testid={`admin-member-name-${member.id}`}>
                            {member.first_name} {member.last_name}
                          </td>
                          <td className="py-2 pr-3 text-muted-foreground" data-testid={`admin-member-email-${member.id}`}>{member.email}</td>
                          <td className="py-2 pr-3" data-testid={`admin-member-role-${member.id}`}>
                            <span className="capitalize">{(member.user_type || '').replace('_', ' ')}</span>
                          </td>
                          <td className="py-2 pr-3" data-testid={`admin-member-admin-${member.id}`}>{member.is_admin ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-3" data-testid={`admin-member-premium-${member.id}`}>{member.is_premium ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-3" data-testid={`admin-member-verified-${member.id}`}>
                            <Badge
                              variant="outline"
                              className={member.verification_status === 'approved' ? 'border-emerald-500/30 text-emerald-300' : 'border-white/20 text-muted-foreground'}
                            >
                              {member.verification_status === 'approved' ? 'Verified' : 'Not Verified'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3" data-testid={`admin-member-status-${member.id}`}>{member.is_active ? 'Active' : 'Inactive'}</td>
                          <td className="py-2 text-muted-foreground" data-testid={`admin-member-created-${member.id}`}>{formatDateTime(member.created_at)}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMemberProfile(member)}
                                data-testid={`admin-member-open-profile-${member.id}`}
                              >
                                Open
                              </Button>
                              {canManageVerification(member) && (
                                <Button
                                  size="sm"
                                  variant={member.verification_status === 'approved' ? 'secondary' : 'outline'}
                                  onClick={() => updateUserVerificationStatus(member, member.verification_status === 'approved' ? 'pending' : 'approved')}
                                  disabled={cleanupTaskLoadingKey === `verification-${member.id}-approved` || cleanupTaskLoadingKey === `verification-${member.id}-pending`}
                                  data-testid={`admin-member-toggle-verified-${member.id}`}
                                >
                                  {member.verification_status === 'approved' ? 'Remove Verified' : 'Grant Verified'}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => archiveUser(member)}
                                disabled={cleanupTaskLoadingKey === `archive-user-${member.id}`}
                                data-testid={`admin-member-archive-${member.id}`}
                              >
                                <Archive className="h-3 w-3 mr-1" />
                                Archive
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteUserPermanently(member)}
                                disabled={cleanupTaskLoadingKey === `delete-user-${member.id}`}
                                data-testid={`admin-member-delete-${member.id}`}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mt-8" data-testid="admin-bartenders-table-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white">Bartenders Table ({bartenderRows.length})</CardTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                <Input
                  placeholder="Search bartenders"
                  value={bartendersSearch}
                  onChange={(event) => setBartendersSearch(event.target.value)}
                  data-testid="admin-bartenders-search-input"
                />
                <Select value={bartendersSort} onValueChange={setBartendersSort}>
                  <SelectTrigger data-testid="admin-bartenders-sort-select"><SelectValue placeholder="Sort bartenders" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="name_asc">Name A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-bartenders-loading-state"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : membersError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-bartenders-error-state">{membersError}</div>
              ) : bartenderRows.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-bartenders-empty-state">No bartenders found for current filters.</div>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-bartenders-table-wrapper">
                  <table className="w-full text-sm" data-testid="admin-bartenders-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-muted-foreground">
                        <th className="py-2 pr-3">Name</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Verified</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bartenderRows.map((member) => (
                        <tr key={member.id} className="border-b border-white/5" data-testid={`admin-bartender-row-${member.id}`}>
                          <td className="py-2 pr-3 text-white">{member.first_name} {member.last_name}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{member.email}</td>
                          <td className="py-2 pr-3" data-testid={`admin-bartender-verified-${member.id}`}>
                            <Badge
                              variant="outline"
                              className={member.verification_status === 'approved' ? 'border-emerald-500/30 text-emerald-300' : 'border-white/20 text-muted-foreground'}
                            >
                              {member.verification_status === 'approved' ? 'Verified' : 'Not Verified'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3">{member.is_active ? 'Active' : 'Suspended'}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => openMemberProfile(member)} data-testid={`admin-bartender-view-${member.id}`}>View</Button>
                              {canManageVerification(member) && (
                                <Button
                                  size="sm"
                                  variant={member.verification_status === 'approved' ? 'secondary' : 'outline'}
                                  onClick={() => updateUserVerificationStatus(member, member.verification_status === 'approved' ? 'pending' : 'approved')}
                                  disabled={cleanupTaskLoadingKey === `verification-${member.id}-approved` || cleanupTaskLoadingKey === `verification-${member.id}-pending`}
                                  data-testid={`admin-bartender-toggle-verified-${member.id}`}
                                >
                                  {member.verification_status === 'approved' ? 'Remove Verified' : 'Grant Verified'}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => archiveUser(member)} disabled={cleanupTaskLoadingKey === `archive-user-${member.id}`} data-testid={`admin-bartender-suspend-${member.id}`}><Archive className="h-3 w-3 mr-1" />Suspend</Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteUserPermanently(member)} disabled={cleanupTaskLoadingKey === `delete-user-${member.id}`} data-testid={`admin-bartender-delete-${member.id}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mt-8" data-testid="admin-owners-table-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white">Owners / Businesses Table ({ownerRows.length})</CardTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                <Input
                  placeholder="Search owners/businesses"
                  value={ownersSearch}
                  onChange={(event) => setOwnersSearch(event.target.value)}
                  data-testid="admin-owners-search-input"
                />
                <Select value={ownersSort} onValueChange={setOwnersSort}>
                  <SelectTrigger data-testid="admin-owners-sort-select"><SelectValue placeholder="Sort owners" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="name_asc">Name A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-owners-loading-state"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : membersError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-owners-error-state">{membersError}</div>
              ) : ownerRows.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-owners-empty-state">No owners/business profiles found for current filters.</div>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-owners-table-wrapper">
                  <table className="w-full text-sm" data-testid="admin-owners-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-muted-foreground">
                        <th className="py-2 pr-3">Name</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Verified</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerRows.map((member) => (
                        <tr key={member.id} className="border-b border-white/5" data-testid={`admin-owner-row-${member.id}`}>
                          <td className="py-2 pr-3 text-white">{member.first_name} {member.last_name}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{member.email}</td>
                          <td className="py-2 pr-3" data-testid={`admin-owner-verified-${member.id}`}>
                            <Badge
                              variant="outline"
                              className={member.verification_status === 'approved' ? 'border-emerald-500/30 text-emerald-300' : 'border-white/20 text-muted-foreground'}
                            >
                              {member.verification_status === 'approved' ? 'Verified' : 'Not Verified'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3">{member.is_active ? 'Active' : 'Suspended'}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => openMemberProfile(member)} data-testid={`admin-owner-view-${member.id}`}>View</Button>
                              {canManageVerification(member) && (
                                <Button
                                  size="sm"
                                  variant={member.verification_status === 'approved' ? 'secondary' : 'outline'}
                                  onClick={() => updateUserVerificationStatus(member, member.verification_status === 'approved' ? 'pending' : 'approved')}
                                  disabled={cleanupTaskLoadingKey === `verification-${member.id}-approved` || cleanupTaskLoadingKey === `verification-${member.id}-pending`}
                                  data-testid={`admin-owner-toggle-verified-${member.id}`}
                                >
                                  {member.verification_status === 'approved' ? 'Remove Verified' : 'Grant Verified'}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => archiveUser(member)} disabled={cleanupTaskLoadingKey === `archive-user-${member.id}`} data-testid={`admin-owner-suspend-${member.id}`}><Archive className="h-3 w-3 mr-1" />Suspend</Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteUserPermanently(member)} disabled={cleanupTaskLoadingKey === `delete-user-${member.id}`} data-testid={`admin-owner-delete-${member.id}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mt-8" data-testid="admin-jobs-table-card">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <CardTitle className="text-xl font-semibold text-white">Jobs Table ({jobsRows.length} of {adminJobsTotal})</CardTitle>
                <Button variant="outline" size="sm" onClick={exportJobsCsv} data-testid="admin-jobs-export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                <Input
                  placeholder="Search jobs by title/location/owner"
                  value={jobsSearch}
                  onChange={(event) => setJobsSearch(event.target.value)}
                  data-testid="admin-jobs-search-input"
                />
                <Select value={jobsStatusFilter} onValueChange={setJobsStatusFilter}>
                  <SelectTrigger data-testid="admin-jobs-status-filter"><SelectValue placeholder="Filter status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={jobsSort} onValueChange={setJobsSort}>
                  <SelectTrigger data-testid="admin-jobs-sort-select"><SelectValue placeholder="Sort jobs" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {adminJobsLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-jobs-loading-state"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : adminJobsError ? (
                <div className="text-center text-red-400 py-6" data-testid="admin-jobs-error-state">{adminJobsError}</div>
              ) : jobsRows.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-jobs-empty-state">No jobs found for current filters.</div>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-jobs-table-wrapper">
                  <table className="w-full text-sm" data-testid="admin-jobs-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-muted-foreground">
                        <th className="py-2 pr-3">Title</th>
                        <th className="py-2 pr-3">Owner</th>
                        <th className="py-2 pr-3">Location</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobsRows.map((job) => (
                        <tr key={job.id} className="border-b border-white/5" data-testid={`admin-job-row-${job.id}`}>
                          <td className="py-2 pr-3 text-white">{job.title || 'Untitled Job'}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{job.bar_owner?.email || 'N/A'}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{job.location?.city || 'N/A'}</td>
                          <td className="py-2 pr-3">{job.is_active ? 'Active' : 'Closed'}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => openJobDetails(job)} data-testid={`admin-job-view-${job.id}`}>View</Button>
                              <Button size="sm" variant="outline" onClick={() => archiveJob(job.id)} disabled={cleanupTaskLoadingKey === `archive-job-${job.id}`} data-testid={`admin-job-suspend-${job.id}`}><Archive className="h-3 w-3 mr-1" />Suspend</Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteJobPermanently(job.id)} disabled={cleanupTaskLoadingKey === `delete-job-${job.id}`} data-testid={`admin-job-delete-${job.id}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5 mt-8" data-testid="admin-instant-shift-table-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white">Instant Shift Fill Table ({instantShiftRows.length})</CardTitle>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                <Input
                  placeholder="Search instant shift requests"
                  value={instantSearch}
                  onChange={(event) => setInstantSearch(event.target.value)}
                  data-testid="admin-instant-shift-search-input"
                />
                <Select value={instantStatusFilter} onValueChange={setInstantStatusFilter}>
                  <SelectTrigger data-testid="admin-instant-shift-status-filter"><SelectValue placeholder="Filter status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={instantSort} onValueChange={setInstantSort}>
                  <SelectTrigger data-testid="admin-instant-shift-sort-select"><SelectValue placeholder="Sort instant shifts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {adminJobsLoading ? (
                <div className="flex items-center justify-center py-6" data-testid="admin-instant-shift-loading-state"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : instantShiftRows.length === 0 ? (
                <div className="text-center text-muted-foreground py-6" data-testid="admin-instant-shift-empty-state">No Instant Shift Fill requests found.</div>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-instant-shift-table-wrapper">
                  <table className="w-full text-sm" data-testid="admin-instant-shift-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-muted-foreground">
                        <th className="py-2 pr-3">Title</th>
                        <th className="py-2 pr-3">Owner</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instantShiftRows.map((job) => (
                        <tr key={job.id} className="border-b border-white/5" data-testid={`admin-instant-shift-row-${job.id}`}>
                          <td className="py-2 pr-3 text-white">{job.title || 'Untitled Shift Request'}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{job.bar_owner?.email || 'N/A'}</td>
                          <td className="py-2 pr-3">{job.is_active ? 'Active' : 'Closed'}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => openJobDetails(job)} data-testid={`admin-instant-shift-view-${job.id}`}>View</Button>
                              <Button size="sm" variant="outline" onClick={() => archiveJob(job.id)} disabled={cleanupTaskLoadingKey === `archive-job-${job.id}`} data-testid={`admin-instant-shift-suspend-${job.id}`}><Archive className="h-3 w-3 mr-1" />Suspend</Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteJobPermanently(job.id)} disabled={cleanupTaskLoadingKey === `delete-job-${job.id}`} data-testid={`admin-instant-shift-delete-${job.id}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="max-w-5xl" data-testid="admin-stat-drilldown-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-stat-drilldown-title">{drilldownTitle}</DialogTitle>
            <DialogDescription data-testid="admin-stat-drilldown-count">
              Showing {drilldownItems.length} of {drilldownTotal}
            </DialogDescription>
            <div className="flex flex-wrap gap-2 pt-2" data-testid="admin-stat-drilldown-time-filters">
              <Button
                size="sm"
                variant={drilldownTimeRange === 'today' ? 'default' : 'outline'}
                onClick={() => {
                  setDrilldownTimeRange('today');
                  loadDrilldownData(drilldownType, 'today');
                }}
                data-testid="admin-stat-drilldown-filter-today"
              >
                Today
              </Button>
              <Button
                size="sm"
                variant={drilldownTimeRange === 'week' ? 'default' : 'outline'}
                onClick={() => {
                  setDrilldownTimeRange('week');
                  loadDrilldownData(drilldownType, 'week');
                }}
                data-testid="admin-stat-drilldown-filter-week"
              >
                This Week
              </Button>
              <Button
                size="sm"
                variant={drilldownTimeRange === 'all' ? 'default' : 'outline'}
                onClick={() => {
                  setDrilldownTimeRange('all');
                  loadDrilldownData(drilldownType, 'all');
                }}
                data-testid="admin-stat-drilldown-filter-all"
              >
                All Time
              </Button>
            </div>
          </DialogHeader>

          {drilldownLoading ? (
            <div className="py-8 flex justify-center" data-testid="admin-stat-drilldown-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : drilldownItems.length === 0 ? (
            <div className="text-center text-muted-foreground py-8" data-testid="admin-stat-drilldown-empty">
              No data found for this section.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto" data-testid="admin-stat-drilldown-content">
              {userDrilldownTypes.includes(drilldownType) && (
                <div className="space-y-2">
                  {drilldownItems.map((member) => (
                    <div key={member.id} className="rounded border border-white/10 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`admin-drilldown-user-${member.id}`}>
                      <div>
                        <p className="text-white font-medium">{`${member.first_name || ''} ${member.last_name || ''}`.trim() || '(No name)'}</p>
                        <p className="text-xs text-muted-foreground">{member.email} • {(member.user_type || '').replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">Joined: {formatDateTime(member.created_at)}</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={() => openProfileFromDrilldown(member)} data-testid={`admin-drilldown-open-profile-${member.id}`}>
                          Open Profile
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => archiveUser(member)} disabled={cleanupTaskLoadingKey === `archive-user-${member.id}`} data-testid={`admin-drilldown-archive-user-${member.id}`}>
                          <Archive className="h-3 w-3 mr-1" />Archive
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteUserPermanently(member)} disabled={cleanupTaskLoadingKey === `delete-user-${member.id}`} data-testid={`admin-drilldown-delete-user-${member.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(drilldownType === 'verifiedBartenders' || drilldownType === 'verifiedBarOwners') && (
                <div className="space-y-2">
                  {drilldownItems.map((entry, index) => (
                    <div key={`${entry.user_id}-${index}`} className="rounded border border-white/10 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`admin-drilldown-verified-${entry.user_id || index}`}>
                      <div>
                        <p className="text-white font-medium">{`${entry.user?.first_name || ''} ${entry.user?.last_name || ''}`.trim() || '(No name)'}</p>
                        <p className="text-xs text-muted-foreground">{entry.user?.email || 'N/A'}</p>
                        <p className="text-xs text-muted-foreground">Verification: {entry.verification_status || 'N/A'} • Joined: {formatDateTime(entry.created_at)}</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={() => openProfileFromDrilldown(entry)} data-testid={`admin-drilldown-open-profile-verified-${entry.user_id || index}`}>
                          Open Profile
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => archiveUser({ id: entry.user?.id, email: entry.user?.email })} disabled={cleanupTaskLoadingKey === `archive-user-${entry.user?.id}`} data-testid={`admin-drilldown-archive-verified-${entry.user_id || index}`}>
                          <Archive className="h-3 w-3 mr-1" />Archive
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteUserPermanently({ id: entry.user?.id, email: entry.user?.email })} disabled={cleanupTaskLoadingKey === `delete-user-${entry.user?.id}`} data-testid={`admin-drilldown-delete-verified-${entry.user_id || index}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(drilldownType === 'totalJobPosts' || drilldownType === 'jobsPostedToday' || drilldownType === 'jobDetails') && (
                <div className="space-y-2">
                  {drilldownItems.map((job) => (
                    <div key={job.id} className="rounded border border-white/10 p-3" data-testid={`admin-drilldown-job-${job.id}`}>
                      <p className="text-white font-medium">{job.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Owner: {job.bar_owner?.name || 'Unknown'} ({job.bar_owner?.email || 'N/A'})
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {job.location?.city || 'N/A'} • ${job.hourly_rate || 0}/hr • {job.is_active ? 'Active' : 'Closed'} • Applications: {job.applications_count || 0}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={() => archiveJob(job.id)} disabled={cleanupTaskLoadingKey === `archive-job-${job.id}`} data-testid={`admin-drilldown-archive-job-${job.id}`}>
                          <Archive className="h-3 w-3 mr-1" />Archive
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteJobPermanently(job.id)} disabled={cleanupTaskLoadingKey === `delete-job-${job.id}`} data-testid={`admin-drilldown-delete-job-${job.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(drilldownType === 'totalApplications' || drilldownType === 'applicationsToday') && (
                <div className="space-y-2">
                  {drilldownItems.map((application) => (
                    <div key={application.id} className="rounded border border-white/10 p-3" data-testid={`admin-drilldown-application-${application.id}`}>
                      <p className="text-white font-medium">{application.job?.title || 'Unknown Job'}</p>
                      <p className="text-xs text-muted-foreground">Applicant: {application.bartender?.name || 'Unknown'} ({application.bartender?.email || 'N/A'})</p>
                      <p className="text-xs text-muted-foreground">Status: {application.status} • Match: {application.match_score ? `${Math.round(application.match_score * 100)}%` : 'N/A'} • Applied: {formatDateTime(application.created_at)}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={() => archiveApplication(application.id)} disabled={cleanupTaskLoadingKey === `archive-app-${application.id}`} data-testid={`admin-drilldown-archive-application-${application.id}`}>
                          <Archive className="h-3 w-3 mr-1" />Archive
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteApplicationPermanently(application.id)} disabled={cleanupTaskLoadingKey === `delete-app-${application.id}`} data-testid={`admin-drilldown-delete-application-${application.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {drilldownType === 'totalMessages' && (
                <div className="space-y-2">
                  {drilldownItems.map((message) => (
                    <div key={message.id} className="rounded border border-white/10 p-3" data-testid={`admin-drilldown-message-${message.id}`}>
                      <p className="text-white font-medium">{message.sender?.name || 'Unknown'} → {message.receiver?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{message.content}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(message.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}

              {drilldownType === 'totalReviews' && (
                <div className="space-y-2">
                  {drilldownItems.map((review) => (
                    <div key={review.id} className="rounded border border-white/10 p-3" data-testid={`admin-drilldown-review-${review.id}`}>
                      <p className="text-white font-medium">{review.bar_owner?.name || 'Unknown'} reviewed {review.bartender?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">Rating: {review.rating} • Status: {review.status}</p>
                      <p className="text-xs text-muted-foreground">{review.comment || '(No comment)'}</p>
                      <div className="mt-2 space-y-2">
                        <label className="text-xs text-muted-foreground" htmlFor={`drilldown-moderation-note-${review.id}`}>
                          Moderation Note (required for Reject/Delete)
                        </label>
                        <Textarea
                          id={`drilldown-moderation-note-${review.id}`}
                          value={reviewModerationNotes[review.id] || ''}
                          onChange={(event) => setReviewModerationNotes((prev) => ({ ...prev, [review.id]: event.target.value }))}
                          placeholder="Add moderation note for this action"
                          className="min-h-[64px]"
                          data-testid={`admin-drilldown-review-note-input-${review.id}`}
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateReviewModerationStatus(review, 'approved')}
                          disabled={cleanupTaskLoadingKey === `review-status-${review.id}-approved` || review.status === 'approved'}
                          data-testid={`admin-drilldown-review-approve-${review.id}`}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateReviewModerationStatus(review, 'rejected')}
                          disabled={cleanupTaskLoadingKey === `review-status-${review.id}-rejected` || review.status === 'rejected'}
                          data-testid={`admin-drilldown-review-reject-${review.id}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteReviewModerationRow(review)}
                          disabled={cleanupTaskLoadingKey === `review-delete-${review.id}`}
                          data-testid={`admin-drilldown-review-delete-${review.id}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                      <details className="mt-2 rounded border border-white/10 bg-black/20 px-2 py-1" data-testid={`admin-drilldown-review-history-collapsible-${review.id}`}>
                        <summary className="cursor-pointer text-xs text-white" data-testid={`admin-drilldown-review-history-toggle-${review.id}`}>
                          Moderation History ({getReviewHistoryEntries(review).length})
                        </summary>
                        <div className="mt-2 space-y-2" data-testid={`admin-drilldown-review-history-panel-${review.id}`}>
                          {getReviewHistoryEntries(review).length === 0 ? (
                            <p className="text-xs text-muted-foreground">No moderation actions yet.</p>
                          ) : getReviewHistoryEntries(review).map((entry) => (
                            <div key={entry.id || `${entry.timestamp}-${entry.action_type}`} className="rounded border border-white/10 p-2" data-testid={`admin-drilldown-review-history-item-${review.id}-${entry.id || entry.timestamp}`}>
                              <p className="text-xs text-white">
                                <span className="capitalize font-medium">{entry.action_type || 'updated'}</span>
                                {' • '}
                                {entry.admin_email || 'Unknown admin'}
                                {' • '}
                                {formatDateTime(entry.timestamp)}
                              </p>
                              <p className="text-xs text-muted-foreground">{entry.previous_status || 'unknown'} → {entry.new_status || 'unknown'}</p>
                              <p className="text-xs text-muted-foreground">{entry.moderation_note || '(No moderation note)'}</p>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[90dvh] overflow-y-auto overscroll-contain p-4 sm:p-6 pb-24 sm:pb-6"
          data-testid="admin-member-profile-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {selectedMember ? `${selectedMember.first_name || ''} ${selectedMember.last_name || ''}`.trim() || 'Member Profile' : 'Member Profile'}
            </DialogTitle>
            <DialogDescription>
              Email: {selectedMember?.email || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          {profileLoading ? (
            <div className="py-8 flex justify-center" data-testid="admin-member-profile-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div
              className="space-y-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
              data-testid="admin-member-profile-content"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded border border-white/10">
                  <p className="text-muted-foreground">Role</p>
                  <p className="text-white capitalize">{(selectedMember?.user_type || '').replace('_', ' ') || 'N/A'}</p>
                </div>
                <div className="p-3 rounded border border-white/10">
                  <p className="text-muted-foreground">Joined</p>
                  <p className="text-white">{formatDateTime(selectedMember?.created_at)}</p>
                </div>
              </div>

              {selectedMemberProfile && (
                <div className="p-3 rounded border border-white/10" data-testid="admin-member-role-profile-block">
                  <p className="text-muted-foreground mb-3">Profile Information</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {'business_name' in selectedMemberProfile ? (
                      <>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-business-name-field"><p className="text-muted-foreground">Business</p><p className="text-white">{selectedMemberProfile.business_name || 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-establishment-type-field"><p className="text-muted-foreground">Establishment Type</p><p className="text-white capitalize">{selectedMemberProfile.establishment_type || 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-location-field"><p className="text-muted-foreground">Location</p><p className="text-white">{selectedMemberProfile.location?.city ? `${selectedMemberProfile.location?.city}, ${selectedMemberProfile.location?.state || ''}` : 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-website-field"><p className="text-muted-foreground">Website</p><p className="text-white break-all">{selectedMemberProfile.website || 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2 md:col-span-2" data-testid="admin-profile-description-field"><p className="text-muted-foreground">Description</p><p className="text-white">{selectedMemberProfile.description || 'Not provided'}</p></div>
                      </>
                    ) : (
                      <>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-experience-field"><p className="text-muted-foreground">Experience</p><p className="text-white">{selectedMemberProfile.experience_years ?? 0} years</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-availability-field"><p className="text-muted-foreground">Available Tonight</p><p className="text-white">{selectedMemberProfile.available_tonight ? 'Yes' : 'No'}</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-location-field"><p className="text-muted-foreground">Location</p><p className="text-white">{selectedMemberProfile.location?.city ? `${selectedMemberProfile.location?.city}, ${selectedMemberProfile.location?.state || ''}` : 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2" data-testid="admin-profile-rate-field"><p className="text-muted-foreground">Rate Range</p><p className="text-white">{selectedMemberProfile.hourly_rate_min || selectedMemberProfile.hourly_rate_max ? `$${selectedMemberProfile.hourly_rate_min || 0} - $${selectedMemberProfile.hourly_rate_max || selectedMemberProfile.hourly_rate_min || 0}` : 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2 md:col-span-2" data-testid="admin-profile-skills-field"><p className="text-muted-foreground">Skills</p><p className="text-white">{selectedMemberProfile.skills?.length ? selectedMemberProfile.skills.join(', ') : 'Not provided'}</p></div>
                        <div className="rounded border border-white/10 p-2 md:col-span-2" data-testid="admin-profile-bio-field"><p className="text-muted-foreground">Bio</p><p className="text-white">{selectedMemberProfile.bio || 'Not provided'}</p></div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {selectedMemberActivity && (
                <div className="p-3 rounded border border-white/10" data-testid="admin-member-activity-block">
                  <p className="text-muted-foreground mb-2">Activity</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {Object.entries(selectedMemberActivity).map(([key, value]) => (
                      <div key={key} className="rounded border border-white/10 p-2" data-testid={`admin-member-activity-field-${key}`}>
                        <p className="text-muted-foreground capitalize">{key.replaceAll('_', ' ')}</p>
                        <p className="text-white">{value ?? 0}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3 rounded border border-white/10" data-testid="admin-verification-audit-timeline-panel">
                <p className="text-muted-foreground mb-2">Verification Audit Timeline</p>
                {selectedMemberVerificationTimeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="admin-verification-audit-empty">No verification actions recorded yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="admin-verification-audit-list">
                    {[...selectedMemberVerificationTimeline]
                      .sort((a, b) => new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime())
                      .map((entry) => (
                        <div key={entry.id || `${entry.timestamp}-${entry.action}`} className="rounded border border-white/10 p-2 text-sm" data-testid={`admin-verification-audit-row-${entry.id || entry.timestamp}`}>
                          <p className="text-white" data-testid={`admin-verification-audit-action-${entry.id || entry.timestamp}`}>
                            {entry.action_label || entry.action || 'Status Updated'}
                          </p>
                          <p className="text-xs text-muted-foreground" data-testid={`admin-verification-audit-admin-${entry.id || entry.timestamp}`}>
                            Admin: {entry.admin_name || entry.admin_email || 'Unknown'}
                          </p>
                          <p className="text-xs text-muted-foreground" data-testid={`admin-verification-audit-transition-${entry.id || entry.timestamp}`}>
                            {entry.previous_status || 'pending'} → {entry.new_status || 'pending'}
                          </p>
                          <p className="text-xs text-muted-foreground" data-testid={`admin-verification-audit-time-${entry.id || entry.timestamp}`}>
                            {formatDateTime(entry.timestamp)}
                          </p>
                          <p className="text-xs text-muted-foreground" data-testid={`admin-verification-audit-note-${entry.id || entry.timestamp}`}>
                            Note: {entry.internal_note || '(No internal note)'}
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="p-3 rounded border border-white/10" data-testid="admin-verification-actions-panel">
                <p className="text-muted-foreground mb-2">Verification Actions</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 text-sm">
                  <div className="rounded border border-white/10 p-2" data-testid="admin-verification-current-status-field">
                    <p className="text-muted-foreground">Current Status</p>
                    <p className="text-white capitalize">{selectedMember?.verification_status || 'pending'}</p>
                  </div>
                  <div className="rounded border border-white/10 p-2" data-testid="admin-verification-last-action-field">
                    <p className="text-muted-foreground">Last Action</p>
                    <p className="text-white capitalize">{selectedMember?.verification_last_action || 'N/A'}</p>
                  </div>
                  <div className="rounded border border-white/10 p-2" data-testid="admin-verification-last-update-field">
                    <p className="text-muted-foreground">Last Updated</p>
                    <p className="text-white">{formatDateTime(selectedMember?.verification_last_updated_at)}</p>
                  </div>
                </div>

                <label className="text-xs text-muted-foreground" htmlFor="admin-verification-admin-note-input">Admin Note (required for Request More Information)</label>
                <Textarea
                  id="admin-verification-admin-note-input"
                  value={verificationAdminNotes[selectedMember?.id] || ''}
                  onChange={(event) => setVerificationAdminNotes((prev) => ({ ...prev, [selectedMember?.id]: event.target.value }))}
                  placeholder="Add verification decision context or request details"
                  className="min-h-[88px] mt-1"
                  data-testid="admin-verification-admin-note-input"
                />

                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    onClick={() => selectedMember && updateUserVerificationStatus(selectedMember, 'approved', 'verify')}
                    disabled={!selectedMember || cleanupTaskLoadingKey.includes(`verification-${selectedMember?.id}`)}
                    data-testid="admin-verification-verify-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />Verify
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => selectedMember && updateUserVerificationStatus(selectedMember, 'rejected', 'reject')}
                    disabled={!selectedMember || cleanupTaskLoadingKey.includes(`verification-${selectedMember?.id}`)}
                    data-testid="admin-verification-reject-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />Reject
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => selectedMember && updateUserVerificationStatus(selectedMember, 'pending', 'request_more_info')}
                    disabled={!selectedMember || cleanupTaskLoadingKey.includes(`verification-${selectedMember?.id}`)}
                    data-testid="admin-verification-request-more-info-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />Request More Information
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;
